/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import UiSwitch from 'components/Ui/UiSwitch'
import UiButton, {UiButtonSize, UiButtonStyle} from 'components/Ui/UiButton'
import {OffersAction} from 'store/offers/OffersAction'
import {ArrowPathIcon, ArrowsRightLeftIcon, DocumentArrowDownIcon, DocumentDuplicateIcon, PlusIcon, TicketIcon, TrashIcon,} from '@heroicons/react/24/outline'
import CreateOfferCalculationModal from 'components/Offers/CreateOfferCalculationModal'
import {closeSecondaryModal} from 'helpers/actions'
import AddItemToCalculationModal from 'components/Offers/AddItemToCalculationModal'
import {ModalSize} from 'types/enums/ModalSize'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import {toast} from 'utils/toastUtils'
import BookingSellerRecapitulationPackageCartItemsTable from 'pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerRecapitulationPackageCartItemsTable'
import type {OfferCalculation} from 'types/entities/OfferCalculation'
import {useAppDispatch} from 'helpers/store'
import {PackageItemItemType} from 'types/enums/PackageItemItemType'
import {OfferStatus} from 'types/enums/OfferStatus'
import {toRoman} from 'utils/utility'
import UiPill from 'components/Ui/UiPill'
import {useVoucherUtilModals} from 'utils/voucherUtilModals'
import {useEnsureOfferAddressForBooking} from 'utils/hooks/useEnsureOfferAddressForBooking'
import {useOfferReservationSuccessModal} from 'utils/hooks/useOfferReservationSuccessModal'
import {useSecondaryConfirmModal} from 'utils/hooks/useSecondaryConfirmModal'
import {useOpenSecondaryModal} from 'utils/hooks/useOpenSecondaryModal'
import {useOptionalOfferDetailContext} from 'pages/Front/Offers/components/OfferDetailContext'
import type {Offer} from 'types/entities/Offer'
import {exportOfferPdf} from 'utils/offers/offerPdfExport'
import {hasOfferReservation} from 'utils/offers/offersUtils'

interface OfferDetailCalculationsProps {
    calc: OfferCalculation
    index: number
    offer?: Offer
}

const OfferCalculationItem: React.FC<OfferDetailCalculationsProps> = ({ calc, index, offer: offerProp }) => {
    const { t } = useTranslation()
    const offerDetailContext = useOptionalOfferDetailContext()
    const offer = offerProp ?? offerDetailContext?.selectedOffer
    const dispatch = useAppDispatch()
    const showReservationCreatedSuccess = useOfferReservationSuccessModal()
    const openConfirmModal = useSecondaryConfirmModal()
    const { openModal } = useOpenSecondaryModal()
    const { onEditCalculationDiscountHandler } = useVoucherUtilModals()

    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)
    const [showDuplicateConfirm, setShowDuplicateConfirm] = React.useState(false)
    const [isDuplicating, setIsDuplicating] = React.useState(false)
    const ensureOfferAddressForBooking = useEnsureOfferAddressForBooking()

    if (!offer) {
        return null
    }

    const isOfferAccepted = offer.status === OfferStatus.Accepted
    const acceptedEditTooltip = t('Disabled because offer is accepted') as string
    const isOfferAddressNotSelected = !offer.address_id
    const hasPackagesCart = !!calc.packagesCart
    const hasExistingReservation = hasOfferReservation(offer)
    const createBookingDisabled = hasExistingReservation || isOfferAddressNotSelected || !hasPackagesCart
    const createBookingTooltip = !hasPackagesCart
        ? t('Calculation has no cart')
        : isOfferAddressNotSelected
          ? '"Address" is not selected.'
          : hasExistingReservation
            ? t('Reservation')
            : undefined
    const calculationNameRaw = calc.name == null ? '' : String(calc.name).trim()
    const calculationLabel = /^\d+$/.test(calculationNameRaw)
        ? toRoman(Number(calculationNameRaw) || index + 1)
        : calculationNameRaw || toRoman(index + 1)
    const offerHasVouchers = calc.packagesCart?.packagesCartItemVouchers?.length > 0
    const hasNoCompany = !calc.packagesCart?.company_id

    const onExportPdfHandler = async (id?: number, isCalculation: boolean = false) => {
        const exportId = id || offer?.id
        if (!exportId) return
        try {
            const exported = await exportOfferPdf({
                dispatch,
                exportId,
                isCalculation,
                isNetto: !!calc.is_netto,
                offerNumber: offer?.number,
                calculationName: calc?.name,
                fallbackCalculationIndex: index + 1,
            })
            if (!exported) {
                toast.error(t('Export failed'))
            }
        } catch (e) {
            console.error(e)
            toast.error(t('Export failed'))
        }
    }

    const createBookingHandler = async (calc: OfferCalculation) => {
        openConfirmModal({
            title: t('Create Booking'),
            message: t('By accepting the offer a valid reservation will be created.'),
            onConfirm: async () => {
                await processBookingCreation(calc)
            },
            confirmStyle: UiButtonStyle.Green,
            modalSize: ModalSize.Small,
            modalTitle: t('Confirm'),
        })
    }

    const processBookingCreation = async (calc: OfferCalculation) => {
        const proceedWithBookingCreation = async () => {
            try {
                const hasAddressForBooking = await ensureOfferAddressForBooking(offer)
                if (!hasAddressForBooking) {
                    return
                }

                const response = await dispatch(
                    OffersAction.fetchCreateReservationFromOffer(offer.id, calc.id, offer.seller_id)
                )

                showReservationCreatedSuccess(response, offer.id)
            } catch (error) {
                console.error(error)
                toast.error(t('Failed to create booking'))
            }
        }

        const totalPeople = (calc.packagesCart.drivers_count_adult || 0) + (calc.packagesCart.drivers_count_child || 0)
        const totalTickets =
            calc.packagesCart.packagesCartItems
                .filter((item) => item.packageItem.type === PackageItemItemType.PackageItem)
                .reduce((sum, item) => {
                    return sum + (item.drivers_count_adult || 0) + (item.drivers_count_child || 0)
                }, 0) || 0

        if (totalPeople !== totalTickets) {
            openConfirmModal({
                title: t('Warning'),
                message: t(
                    'The number of people ({{people}}) does not match the number of access tickets ({{tickets}}). Do you want to continue?',
                    { people: totalPeople, tickets: totalTickets }
                ),
                onConfirm: async () => {
                    await proceedWithBookingCreation()
                },
                modalSize: ModalSize.Small,
                modalTitle: t('Warning'),
            })
            return
        }

        await proceedWithBookingCreation()
    }

    const deleteCalculationHandler = () => {
        setShowDeleteConfirm(true)
    }

    const onDuplicateCalculationHandler = () => {
        setShowDuplicateConfirm(true)
    }

    const addNewItemToCalculationHandler = () => {
        if (!hasPackagesCart) {
            toast.error(t('Calculation has no cart'))
            return
        }

        openModal(
            <AddItemToCalculationModal
                offerId={offer.id}
                calculation={calc}
                onSuccess={() => {
                    // Offer is updated via Redux action in the modal
                }}
            />,
            {
                modalSize: ModalSize.RoundedFullscreen,
            },
            t('Add New Item')
        )
    }

    const selectCalculationAsTargetHandler = () => {
        openConfirmModal({
            title: t('Select as Target'),
            message: offer.calculations?.some((c) => c.is_target)
                ? t(
                      'Another calculation is already set as target. Setting this one will unset the previous one. Do you want to continue?'
                  )
                : t('Do you want to set this calculation as target?'),
            onConfirm: async () => {
                try {
                    const existingTarget = offer.calculations?.find((c) => c.is_target)
                    if (existingTarget) {
                        await dispatch(
                            OffersAction.fetchAddOrEditOfferCalculation(existingTarget.id, offer.id, offer.seller_id, {
                                is_target: false,
                                probability: 50,
                                packages_cart_id: existingTarget.packages_cart_id,
                            })
                        )
                    }
                    await dispatch(
                        OffersAction.fetchAddOrEditOfferCalculation(calc.id, offer.id, offer.seller_id, {
                            is_target: true,
                            probability: 100,
                            packages_cart_id: calc.packages_cart_id,
                        })
                    )
                    toast.success(t('Target calculation set successfully'))
                    dispatch(closeSecondaryModal())
                } catch (error) {
                    console.error(error)
                    toast.error(t('Failed to set target calculation'))
                }
            },
            modalSize: ModalSize.Small,
            modalTitle: t('Confirm'),
        })
    }
    const onChangeIsNettoOnCalculationHandler = (e) => {
        dispatch(
            OffersAction.fetchAddOrEditOfferCalculation(calc.id, calc.offer_id, offer.seller_id, {
                is_target: calc.is_target,
                packages_cart_id: calc.packages_cart_id,
                is_netto: e.target.checked,
            })
        ).then(() => {
            toast.success(t('Calculation has been updated'), { toastId: 'onChangeIsNettoOnCalculationHandler' })
            // dispatch(OffersAction.fetchOfferById(offer.id))
        })
    }
    const onRestoreCalculationFromDeletedHandler = () => {
        openConfirmModal({
            title: t('Undelete Calculation'),
            message: t('Do you want to restore this calculation to active?'),
            onConfirm: async () => {
                try {
                    await dispatch(OffersAction.fetchUndeleteOfferCalculation(calc.id))
                    toast.success(t('Calculation restored successfully'))
                    dispatch(closeSecondaryModal())
                } catch (error) {
                    console.error(error)
                    toast.error(t('Failed to restore calculation'))
                }
            },
            modalSize: ModalSize.Small,
            modalTitle: t('Confirm'),
        })
    }

    return (
        <div
            className={clsx(
                'OfferCalculationItem flex flex-col bg-white dark:bg-gray-900 rounded-xl p-3 shadow-md hover:shadow-lg transition-all relative hover:z-30 overflow-hidden',
                calc.is_target
                    ? 'ring-2 ring-blue-500 dark:ring-blue-400 border-2 border-blue-400 dark:border-blue-500'
                    : calc.deleted_at
                      ? 'border-2 border-red-100 dark:border-red-900/30'
                      : 'border-2 border-gray-200 dark:border-gray-700'
            )}
        >
            {showDeleteConfirm && (
                <div
                    className="absolute inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-gray-900/90 rounded-xl backdrop-blur-sm p-4"
                    onClick={(event) => event.stopPropagation()}
                >
                    <UiAlertConfirmationModal
                        title={t('Delete Calculation')}
                        message={t(
                            'Are you sure you want to delete this calculation? You can restore it later from the deleted calculations.'
                        )}
                        onConfirm={async () => {
                            try {
                                await dispatch(OffersAction.fetchDeleteOfferCalculation(calc.id))
                                toast.success(t('Calculation deleted successfully'))
                                setShowDeleteConfirm(false)
                            } catch (error) {
                                toast.error(t('Failed to delete calculation ' + String(error)))
                            }
                        }}
                        onCancel={() => setShowDeleteConfirm(false)}
                        confirmStyle={UiButtonStyle.Red}
                    />
                </div>
            )}
            {showDuplicateConfirm && (
                <div
                    className="absolute inset-0 z-50 flex items-center justify-center bg-white/90 dark:bg-gray-900/90 rounded-xl backdrop-blur-sm p-4"
                    onClick={(event) => event.stopPropagation()}
                >
                    <UiAlertConfirmationModal
                        title={t('Duplicate calculation')}
                        message={t('Are you sure you want to duplicate this calculation?')}
                        onConfirm={async () => {
                            try {
                                setIsDuplicating(true)
                                await dispatch(
                                    OffersAction.fetchDuplicateOfferCalculation(calc.id, offer.id, offer.seller_id)
                                )
                                toast.success(t('Calculation duplicated successfully'))
                                setShowDuplicateConfirm(false)
                            } catch (e) {
                                console.error(e)
                                toast.error(t('Failed to duplicate calculation'))
                            } finally {
                                setIsDuplicating(false)
                            }
                        }}
                        onCancel={() => setShowDuplicateConfirm(false)}
                        isSubmitting={isDuplicating}
                    />
                </div>
            )}
            <div
                className={clsx(
                    'CalculationItemInner flex flex-col flex-1 relative z-10',
                    calc.deleted_at && 'opacity-50 grayscale-[0.5]'
                )}
            >
                <div
                    className={clsx(
                        'CalculationItemInnerHeader flex items-center justify-between mb-3 pb-2 border-b border-gray-100 dark:border-gray-800 shrink-0'
                    )}
                >
                    <div className="flex items-center justify-center gap-2">
                        <UiPill className="rounded-md bg-linear-to-br from-blue-500 to-blue-600 text-white font-bold text-sm shadow-sm px-2">
                            {t('Calc')} {calculationLabel}
                        </UiPill>

                        {calc.is_target && (
                            <UiPill className={'bg-linear-to-r from-blue-500 to-blue-600 text-white text-xs font-bold'}>
                                <span className={'uppercase'}>{t('target')}</span>
                            </UiPill>
                        )}
                        {calc.deleted_at && (
                            <div className="z-10 inline-flex">
                                <div className="bg-red-500 text-white text-[10px] uppercase font-bold px-3 py-1 rounded-full shadow-sm">
                                    {t('Deleted')}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <span
                            className={clsx(
                                'text-[10px] uppercase font-bold',
                                !calc.is_netto ? 'text-blue-600' : 'text-gray-400'
                            )}
                        >
                            {t('Brutto')}
                        </span>
                        <UiSwitch
                            name="is_netto"
                            size="sm"
                            checked={calc.is_netto}
                            onChange={onChangeIsNettoOnCalculationHandler}
                            disabled={isOfferAccepted || !!calc.deleted_at} // also locked if accepted
                        />
                        <span
                            className={clsx(
                                'text-[10px] uppercase font-bold',
                                calc.is_netto ? 'text-blue-600' : 'text-gray-400'
                            )}
                        >
                            {t('Netto')}
                        </span>
                    </div>
                    {!calc.deleted_at && (
                        <div className="flex gap-1.5">
                            <UiButton
                                onClick={() => {
                                    if (!calc.packagesCart?.company_id || isOfferAccepted) return
                                    onDuplicateCalculationHandler()
                                }}
                                size={UiButtonSize.Small}
                                style={UiButtonStyle.Cyan}
                                data-tooltip-id="calculation-item-action-tooltip"
                                data-tooltip-content={
                                    isOfferAccepted
                                        ? acceptedEditTooltip
                                        : !calc.packagesCart?.company_id
                                          ? t('Calculation has no company')
                                          : t('Duplicate')
                                }
                                disabled={isOfferAccepted}
                            >
                                <DocumentDuplicateIcon className="size-4" />
                            </UiButton>
                            <UiButton
                                onClick={() => {
                                    openModal(
                                        <CreateOfferCalculationModal
                                            offer_id={offer.id}
                                            is_secondary={true}
                                            calculationId={calc.id}
                                            onCreated={() => {
                                                dispatch(OffersAction.fetchOfferById(offer.id))
                                            }}
                                        />,
                                        { modalSize: ModalSize.RoundedFullscreen },
                                        t('Switch Package Category')
                                    )
                                }}
                                size={UiButtonSize.Small}
                                style={isOfferAccepted ? UiButtonStyle.Gray : UiButtonStyle.Indigo}
                                data-tooltip-id="calculation-item-action-tooltip"
                                data-tooltip-content={
                                    isOfferAccepted ? acceptedEditTooltip : t('Switch Package Category')
                                }
                                disabled={isOfferAccepted}
                            >
                                <ArrowsRightLeftIcon className="size-4" />
                            </UiButton>
                            <UiButton
                                onClick={() => {
                                    if (!calc.packagesCart) return
                                    onEditCalculationDiscountHandler(calc, calc.packagesCart, offer)
                                }}
                                size={UiButtonSize.Small}
                                style={
                                    isOfferAccepted
                                        ? UiButtonStyle.Gray
                                        : offerHasVouchers
                                          ? UiButtonStyle.Gray
                                          : UiButtonStyle.Yellow
                                }
                                dataTooltipId={isOfferAccepted ? 'calculation-item-action-tooltip' : undefined}
                                dataTooltipContent={isOfferAccepted ? acceptedEditTooltip : undefined}
                                disabled={isOfferAccepted || !hasPackagesCart}
                            >
                                <TicketIcon
                                    className={clsx(
                                        'size-4 text-inherit dark:text-inherit'

                                        // isOfferAccepted
                                        //     ? 'text-gray-400'
                                        //     : offerHasVouchers
                                        //       ? 'text-gray-400 dark:text-gray-300'
                                        //       : 'text-yellow-700 dark:text-yellow-300'
                                    )}
                                />
                            </UiButton>
                            <UiButton
                                onClick={addNewItemToCalculationHandler}
                                style={UiButtonStyle.Blue}
                                size={UiButtonSize.Small}
                                dataTooltipId={isOfferAccepted ? 'calculation-item-action-tooltip' : undefined}
                                dataTooltipContent={isOfferAccepted ? acceptedEditTooltip : undefined}
                                disabled={isOfferAccepted}
                            >
                                <PlusIcon className={clsx('size-4')} />
                            </UiButton>
                            <UiButton
                                onClick={deleteCalculationHandler}
                                style={isOfferAccepted ? UiButtonStyle.Gray : UiButtonStyle.Red}
                                size={UiButtonSize.Small}
                                dataTooltipId={isOfferAccepted ? 'calculation-item-action-tooltip' : undefined}
                                dataTooltipContent={isOfferAccepted ? acceptedEditTooltip : undefined}
                                className={clsx(
                                    'p-1.5 rounded-lg transition-colors',
                                    isOfferAccepted
                                        ? 'bg-gray-100 dark:bg-gray-800 opacity-50 cursor-not-allowed'
                                        : 'bg-red-100 hover:bg-red-200 dark:bg-red-800 dark:hover:bg-red-700'
                                )}
                                disabled={isOfferAccepted}
                            >
                                <TrashIcon
                                    className={clsx(
                                        'size-4',
                                        isOfferAccepted ? 'text-red-700 dark:text-red-300' : 'text-white'
                                    )}
                                />
                            </UiButton>
                        </div>
                    )}
                </div>
                <div className="CalculationItemInnerList shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm max-h-100 overflow-y-auto overflow-x-hidden relative">
                    {calc.is_target && (
                        <div
                            className="absolute inset-0 z-20 pointer-events-none select-none"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '60px',
                                    fontWeight: 'bold',
                                    color: '#3b82f6',
                                    opacity: 0.05,
                                    transform: 'rotate(-20deg)',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {t('target')}
                            </div>
                        </div>
                    )}
                    <div style={{ position: 'relative', zIndex: 10 }}>
                        {hasPackagesCart ? (
                            <BookingSellerRecapitulationPackageCartItemsTable
                                title=""
                                offer={offer}
                                calculation={calc}
                                disabled={!!calc.deleted_at || isOfferAccepted}
                                // isNetto={!!calc.is_netto}
                                // calculationPriceTotal={calc.packagesCart?.price ?? undefined}
                                // discount={calc.packagesCart?.prices?.discount || 0}

                                // disabledEditTooltip={isOfferAccepted ? acceptedEditTooltip : undefined}
                            />
                        ) : (
                            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                {t('No cart assigned to this calculation')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!calc.deleted_at ? (
                <div className="flex items-center gap-2 flex-wrap sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 py-3 z-10 mt-auto">
                    <UiButton
                        onClick={() => {
                            if (!calc.packagesCart?.company_id) return
                            onExportPdfHandler(calc.id, true)
                        }}
                        size={UiButtonSize.Normal}
                        style={hasNoCompany ? UiButtonStyle.Gray : UiButtonStyle.Indigo}
                        disabled={hasNoCompany}
                    >
                        <DocumentArrowDownIcon className="size-4" />
                        {t('EXPORT PDF')}
                    </UiButton>
                    {!calc.is_target ? (
                        <UiButton
                            disabled={isOfferAccepted}
                            size={UiButtonSize.Normal}
                            style={isOfferAccepted ? UiButtonStyle.Gray : UiButtonStyle.Blue}
                            dataTooltipId={isOfferAccepted ? 'calculation-item-action-tooltip' : undefined}
                            dataTooltipContent={isOfferAccepted ? acceptedEditTooltip : undefined}
                            onClick={selectCalculationAsTargetHandler}
                        >
                            {t('Select as Target')}
                        </UiButton>
                    ) : (
                        <UiButton
                            onClick={() => createBookingHandler(calc)}
                            size={UiButtonSize.Normal}
                            style={createBookingDisabled ? UiButtonStyle.Gray : UiButtonStyle.GradientGreen}
                            disabled={createBookingDisabled}
                            dataTooltipId={createBookingTooltip ? 'calculation-item-action-tooltip' : undefined}
                            dataTooltipContent={createBookingTooltip}
                            title={t('Create Booking')}
                        />
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-2 flex-wrap sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 py-3 z-10 mt-auto">
                    <button
                        onClick={onRestoreCalculationFromDeletedHandler}
                        className="px-3 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-all flex items-center gap-2 shrink-0"
                    >
                        <ArrowPathIcon className="size-4" />
                        {t('Restore Calculation')}
                    </button>
                </div>
            )}
        </div>
    )
}

export default OfferCalculationItem
